import * as React from "react";
import { cn } from "@/components/podkit/lib/cn";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { ChevronDown, Check } from "lucide-react";

export type TimePickerProps = {
    value?: { hour: number; minute: number };
    onChange?: (time: { hour: number; minute: number }) => void;
    className?: string;
    disabled?: boolean;
    readOnly?: boolean;
    timezone?: string;
    showTimezone?: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export enum Period {
    AM = "AM",
    PM = "PM",
}

// eslint-disable-next-line react-refresh/only-export-components
export const convert24To12Hour = (hour24: number): { hour12: number; period: Period } => {
    if (hour24 === 0) return { hour12: 12, period: Period.AM };
    if (hour24 < 12) return { hour12: hour24, period: Period.AM };
    if (hour24 === 12) return { hour12: 12, period: Period.PM };
    return { hour12: hour24 - 12, period: Period.PM };
};

// eslint-disable-next-line react-refresh/only-export-components
export const convert12To24Hour = (hour12: number, period: Period): number => {
    if (period === Period.AM) {
        return hour12 === 12 ? 0 : hour12;
    } else {
        return hour12 === 12 ? 12 : hour12 + 12;
    }
};

// eslint-disable-next-line react-refresh/only-export-components
export const normalizeHourInput = (
    inputValue: number,
    currentHour12: number,
    currentPeriod: Period,
): { hour12: number; period: Period } => {
    let val = inputValue;
    let newPeriod = currentPeriod;

    // Handle 24-hour format input (13-23)
    if (val >= 13 && val <= 23) {
        const converted = convert24To12Hour(val);
        val = converted.hour12;
        newPeriod = converted.period;
    } else if (val === 0) {
        // Midnight: 0 -> 12 AM
        val = 12;
        newPeriod = Period.AM;
    } else if (val === 24) {
        // 24 -> 12 AM (midnight)
        val = 12;
        newPeriod = Period.AM;
    } else if (isNaN(val) || val < 1 || val > 24) {
        // Reset to valid value
        val = currentHour12;
        newPeriod = currentPeriod;
    }

    return { hour12: val, period: newPeriod };
};

export const TimePicker: React.FC<TimePickerProps> = ({
    value,
    onChange,
    className,
    disabled = false,
    readOnly = false,
    timezone = "UTC",
    showTimezone = true,
}) => {
    const hour24 = value?.hour ?? 0;
    const minute = value?.minute ?? 0;

    const { hour12, period } = convert24To12Hour(hour24);

    // Local state for input values to avoid losing focus
    const [hourInput, setHourInput] = React.useState(String(hour12).padStart(2, "0"));
    const [minuteInput, setMinuteInput] = React.useState(String(minute).padStart(2, "0"));
    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    // Refs for focusing
    const hourInputRef = React.useRef<HTMLInputElement>(null);
    const minuteInputRef = React.useRef<HTMLInputElement>(null);
    const periodButtonRef = React.useRef<HTMLButtonElement>(null);

    // Track if inputs are focused to avoid overwriting user input
    const [hourFocused, setHourFocused] = React.useState(false);
    const [minuteFocused, setMinuteFocused] = React.useState(false);

    // Update local state when value prop changes, but only if not focused
    React.useEffect(() => {
        if (!hourFocused) {
            setHourInput(String(hour12).padStart(2, "0"));
        }
    }, [hour12, hourFocused]);

    React.useEffect(() => {
        if (!minuteFocused) {
            setMinuteInput(String(minute).padStart(2, "0"));
        }
    }, [minute, minuteFocused]);

    const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        // Allow empty or numeric input
        if (newValue === "" || /^\d{0,2}$/.test(newValue)) {
            setHourInput(newValue);
        }
    };

    const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        // Allow empty or numeric input
        if (newValue === "" || /^\d{0,2}$/.test(newValue)) {
            setMinuteInput(newValue);
        }
    };

    const handlePeriodChange = (newPeriod: Period) => {
        const newHour24 = convert12To24Hour(hour12, newPeriod);
        onChange?.({ hour: newHour24, minute });
    };

    const handleHourFocus = () => {
        setHourFocused(true);
    };

    const handleHourBlur = () => {
        setHourFocused(false);
        const inputValue = parseInt(hourInput);
        const normalized = normalizeHourInput(inputValue, hour12, period);

        const paddedValue = String(normalized.hour12).padStart(2, "0");
        setHourInput(paddedValue);

        const newHour24 = convert12To24Hour(normalized.hour12, normalized.period);
        onChange?.({ hour: newHour24, minute });
    };

    const handleMinuteFocus = () => {
        setMinuteFocused(true);
    };

    const handleMinuteBlur = () => {
        setMinuteFocused(false);
        let val = parseInt(minuteInput);
        if (isNaN(val) || val < 0 || val > 59) {
            // Reset to valid value
            val = minute;
        }
        const paddedValue = String(val).padStart(2, "0");
        setMinuteInput(paddedValue);

        onChange?.({ hour: hour24, minute: val });
    };

    const handleHourKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.currentTarget.blur();
        } else if (e.key === ":" || e.key === ";") {
            e.preventDefault();
            minuteInputRef.current?.focus();
            minuteInputRef.current?.select();
        } else if (e.key === "ArrowRight") {
            e.preventDefault();
            minuteInputRef.current?.focus();
            minuteInputRef.current?.select();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            let newHour12 = hour12 + 1;
            let newPeriod = period;
            if (newHour12 > 12) {
                // Wrap from 12 to 1 (period stays the same)
                newHour12 = 1;
            } else if (newHour12 === 12 && hour12 === 11) {
                // Special case: 11 -> 12 should toggle period (11 AM -> 12 PM, 11 PM -> 12 AM)
                newPeriod = period === Period.AM ? Period.PM : Period.AM;
            }
            // Update local state immediately for programmatic changes
            setHourInput(String(newHour12).padStart(2, "0"));
            const newHour24 = convert12To24Hour(newHour12, newPeriod);
            onChange?.({ hour: newHour24, minute });
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            let newHour12 = hour12 - 1;
            let newPeriod = period;
            if (newHour12 < 1) {
                // Wrap from 1 to 12 (period stays the same)
                newHour12 = 12;
            } else if (newHour12 === 11 && hour12 === 12) {
                // Special case: 12 -> 11 should toggle period (12 PM -> 11 AM, 12 AM -> 11 PM)
                newPeriod = period === Period.AM ? Period.PM : Period.AM;
            }
            // Update local state immediately for programmatic changes
            setHourInput(String(newHour12).padStart(2, "0"));
            const newHour24 = convert12To24Hour(newHour12, newPeriod);
            onChange?.({ hour: newHour24, minute });
        }
        // ArrowLeft does nothing in hour field (already at start)
    };

    const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.currentTarget.blur();
        } else if (e.key === "Tab" && !e.shiftKey) {
            e.preventDefault();
            handleMinuteBlur();
            setDropdownOpen(true);
            // Focus the period button after a short delay to ensure dropdown is open
            setTimeout(() => {
                periodButtonRef.current?.focus();
            }, 0);
        } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            handleMinuteBlur();
            hourInputRef.current?.focus();
            hourInputRef.current?.select();
        } else if (e.key === "ArrowRight") {
            e.preventDefault();
            handleMinuteBlur();
            setDropdownOpen(true);
            // Focus the period button after a short delay to ensure dropdown is open
            setTimeout(() => {
                periodButtonRef.current?.focus();
            }, 0);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            let newMinute = minute + 1;
            if (newMinute > 59) newMinute = 0;
            // Update local state immediately for programmatic changes
            setMinuteInput(String(newMinute).padStart(2, "0"));
            onChange?.({ hour: hour24, minute: newMinute });
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            let newMinute = minute - 1;
            if (newMinute < 0) newMinute = 59;
            // Update local state immediately for programmatic changes
            setMinuteInput(String(newMinute).padStart(2, "0"));
            onChange?.({ hour: hour24, minute: newMinute });
        }
    };

    const handlePeriodKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === " " || e.key === "Enter") {
            e.preventDefault();
            setDropdownOpen(true);
        } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            minuteInputRef.current?.focus();
            minuteInputRef.current?.select();
        }
        // ArrowRight does nothing in period field (already at end)
    };

    const isInteractive = !disabled && !readOnly;

    return (
        <div className={cn("inline-flex items-center gap-2", className)}>
            <div
                className={cn(
                    "inline-flex items-center",
                    "h-8 pl-2 pr-3 rounded-lg",
                    "border border-border-base",
                    readOnly ? "bg-surface-muted" : "bg-surface-pure",
                    isInteractive && "focus-within:outline focus-within:outline-1 focus-within:outline-border-brand",
                    disabled && "opacity-50 pointer-events-none",
                    readOnly && "pointer-events-none",
                )}
            >
                <input
                    ref={hourInputRef}
                    type="text"
                    inputMode="numeric"
                    value={hourInput}
                    onChange={handleHourChange}
                    onFocus={handleHourFocus}
                    onBlur={handleHourBlur}
                    onKeyDown={handleHourKeyDown}
                    disabled={disabled}
                    readOnly={readOnly}
                    className={cn("w-6 text-sm text-center bg-transparent border-0 outline-none", "focus:ring-0 p-0")}
                    maxLength={2}
                    aria-label="Hour"
                />
                <span className="text-sm text-content-secondary">:</span>
                <input
                    ref={minuteInputRef}
                    type="text"
                    inputMode="numeric"
                    value={minuteInput}
                    onChange={handleMinuteChange}
                    onFocus={handleMinuteFocus}
                    onBlur={handleMinuteBlur}
                    onKeyDown={handleMinuteKeyDown}
                    disabled={disabled}
                    readOnly={readOnly}
                    className={cn("w-6 text-sm text-center bg-transparent border-0 outline-none", "focus:ring-0 p-0")}
                    maxLength={2}
                    aria-label="Minute"
                />
                <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                    <DropdownMenuTrigger
                        ref={periodButtonRef}
                        disabled={disabled || readOnly}
                        onKeyDown={handlePeriodKeyDown}
                        className={cn(
                            "ml-1 inline-flex items-center gap-1",
                            "text-sm bg-transparent",
                            isInteractive && "hover:text-content-accent",
                            "focus:outline-none focus-visible:ring-0",
                        )}
                        data-tracking-id="time-picker-period"
                    >
                        {period}
                        {!readOnly && <ChevronDown className="h-3 w-3 opacity-50" />}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" alignOffset={-8} className="w-20 min-w-0">
                        <DropdownMenuItem
                            onClick={() => {
                                handlePeriodChange(Period.AM);
                                setDropdownOpen(false);
                            }}
                            data-tracking-id="time-picker-period-am"
                            className="justify-between pr-2"
                        >
                            <span>AM</span>
                            {period === Period.AM && <Check className="h-4 w-4" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => {
                                handlePeriodChange(Period.PM);
                                setDropdownOpen(false);
                            }}
                            data-tracking-id="time-picker-period-pm"
                            className="justify-between pr-2"
                        >
                            <span>PM</span>
                            {period === Period.PM && <Check className="h-4 w-4" />}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            {showTimezone && (
                <span className={cn("text-sm text-content-secondary", disabled && "opacity-50")}>{timezone}</span>
            )}
        </div>
    );
};
